import React, { useEffect, useRef, useState } from 'react';
import { Table } from '@cokitchen/cokitchen-components';
import { EventStatementType, EventReportType, EventSummaryType } from './types';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import {
  formatEventStatementCellValue,
  getFirstDayOfYear,
  getLastDayOfYear,
  handleApiError
} from 'utils/functions';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import StatementSummary from './statement-summary';
import PaymentTableRow from './table-row';

const tableHeaders = ['Name', 'Start Date', 'End Date', 'Opening Balance', 'Closing Balance', ''];

type Props = {
  filter: string;
};

const EventsTable = ({ filter }: Props) => {
  const { activeCompany } = useDashboardContext();
  const pdfSummaryRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<EventStatementType[]>([]);
  const [loading, setLoading] = useState(false);
  const [pdfSummary, setPdfSummary] = useState<EventSummaryType | null>(null);

  const loadMonthlyStatement = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`admin/event-statement`, {
        params: {
          company_id: activeCompany.id,
          start_date: getFirstDayOfYear(filter),
          end_date: getLastDayOfYear(filter)
        }
      });
      setData(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPdfSummary = async (event_id: string) => {
    try {
      const res = await apiService.get(`admin/event-summary/${event_id}`);
      const summary = res.data?.data;
      setPdfSummary(summary);
    } catch (error) {
      console.log(error);
    }
  };

  const downloadStatement = async (event_id: string, format: string) => {
    try {
      await loadPdfSummary?.(event_id);
      const res = await apiService.get(`admin/event-report/${event_id}`);
      const data = res.data?.data?.map((data: EventReportType, index: number) => ({
        sn: index + 1,
        full_name: `${data.first_name} ${data.last_name}`,
        ...data
      }));
      if (format === 'xlsx') downloadXlsx(data);
      if (format === 'pdf') downloadPdf(data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setPdfSummary(null);
    }
  };

  const downloadXlsx = (data: EventReportType[]) => {
    const filename = `Event-Statement.xlsx`;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, filename);
  };

  const downloadPdf = async (data: EventReportType[]) => {
    const doc = new jsPDF({ unit: 'px' });
    if (pdfSummaryRef.current) {
      const canvas = await html2canvas(pdfSummaryRef.current, { scale: 10 });
      const imageData = canvas.toDataURL('image/jpeg');
      doc.addImage({ imageData, x: 0, y: 0, width: 446, height: 240, format: 'JPEG' });
    }
    const columns = [
      { header: 'S/N', dataKey: 'sn' },
      { header: 'Guest names', dataKey: 'full_name' },
      { header: 'Phone number', dataKey: 'phone_number' },
      { header: 'Email address', dataKey: 'email' },
      { header: 'Amount Allocated (N)', dataKey: 'total_allocation' },
      { header: 'Amount Spent (N)', dataKey: 'total_spent' }
    ];
    autoTable(doc, {
      head: [columns.map((col) => col.header)],
      body: data.map((row) => columns.map((col) => formatEventStatementCellValue(col, row))),
      styles: { fontSize: 10 },
      headStyles: { fillColor: '#191919' },
      margin: { left: 16, right: 16 },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 70 },
        2: { cellWidth: 70 },
        3: { cellWidth: 115 },
        4: { cellWidth: 70 },
        5: { cellWidth: 70 }
      },
      startY: 240
    });
    doc.save(`Event-Statement.pdf`);
  };

  useEffect(() => {
    if (!activeCompany.id) return;
    loadMonthlyStatement();
  }, []);

  return (
    <>
      {!loading && data.length === 0 ? (
        <div className='mt-8 flex items-center'>
          <p className='mt-1'>Company has no transactions for {filter}</p>
        </div>
      ) : (
        <Table headers={tableHeaders} loading={loading}>
          {data?.map((statement: EventStatementType, index: number) => (
            <PaymentTableRow key={index} statement={statement} handleDownload={downloadStatement} />
          ))}
        </Table>
      )}
      {pdfSummary ? <StatementSummary elementRef={pdfSummaryRef} summary={pdfSummary} /> : null}
    </>
  );
};

export default EventsTable;
